import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/* ---------------------------------- devex --------------------------------- */
import DataGrid, {
  Column,
  ColumnChooser,
  ColumnFixing,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  Grouping,
  GroupItem,
  GroupPanel,
  HeaderFilter,
  Item,
  LoadPanel,
  Paging,
  Scrolling,
  Selection,
  Sorting,
  StateStoring,
  Summary,
  Toolbar,
  TotalItem,
} from "devextreme-react/data-grid";
import TextBox, { TextBoxRef } from "devextreme-react/text-box";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { exportDataGrid } from "devextreme/excel_exporter";
import { call as eCall, ensure, run, sleep } from "effection";
//excel
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import sum from "hash-sum";
import { filter as jscFilter, singleton, yielding } from "js-coroutines";
import pick from "lodash/fp/pick";
/* --------------------------------- helpers -------------------------------- */
import size from "lodash/fp/size";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import resso from "resso";
import { useDebouncedCallback } from "use-debounce";

import { useWindowDimensions } from "@app/contexts";
import {
  CONST_LISTORIGIN_INVOICE,
  CONST_LISTORIGIN_PROFORMA,
  GRID_LAYOUTS,
} from "@app/state/insider/constants";
/* ---------------------------------- STATE --------------------------------- */
import { listSwitchSelector } from "@app/state/insider/lists";
import {
  selectListReportCurrentState,
  slectListReportLoadedID,
  uiListTempUiSetMenuOpened_afterSync,
  uiListTempUiSetMenuOpened_withSyncOption,
} from "@app/state/insider/ui/uiList";
import "@app/style/dx.generic.jurnalKarma1.css";
/* -------------------------------------------------------------------------- */
//style
import "@app/style/dx.generic.teamZebra.css";
import { IListState, TListOriginName } from "@app/types/insider";

import { useAppSelector } from "../../state";
import "./grids_common.scss";
//datagrid helpers
import {
  DOMPurify,
  fieldDefs,
  mark,
  markRegexParts,
  md,
  parts,
  regexParts,
  SEARCH_MIN_CHARS_NUMBER,
} from "./lxr_common_definitions";
import {
  calculateTotalValues,
  groupCustomItem,
  groupCustomTotals,
  totalsCount,
} from "./lxr_common_elements";

import type { DataGridRef } from "devextreme-react/data-grid";
/* -------------------------------------------------------------------------- */
import type { Operation } from "effection";
//local types
type TWhichState = "initial" | "current" | IListState;

interface FieldDefinitions {
  [key: string]: any;
}
/* -------------------------------------------------------------------------- */
/*                                  THE GRID                                  */
/* -------------------------------------------------------------------------- */
const local = resso({
  expectedGridState: {},
  xrender: 0,
});

const InvoiceGrid = ({ listOrigin }: { listOrigin: TListOriginName }) => {
  const { expectedGridState, xrender } = local;
  const dispatch = useDispatch();
  const effRef5 = useRef<boolean>(false);
  const savedStateRef = useRef<IListState>({});
  const searchStringRef = useRef<string>();
  const tsSearchBoxRef = useRef<TextBoxRef>();
  const loadedColumnsRef = useRef<string[]>([]);
  const [dataSet, setDataset] = useState([]);
  const [layoutID, setLayoutID] = useState<number>(1);
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinitions>();
  const [tsSrchBoxValue, setTsSearchValue] = useState<string>("");
  const theState = useAppSelector(selectListReportCurrentState);
  const theLoadedListID = useAppSelector(slectListReportLoadedID);
  const theData = useAppSelector((s) =>
    listSwitchSelector(s, { list: listOrigin })
  );
  // const { anyListGridRef: tsGridRef, openPdf, friendlyName } = usePdfContext();
  const tsGridRef = useRef<DataGridRef>();
  const { t } = useTranslation();
  const {
    height: fullHeight,
    headersFooters,
    drawerHeight,
  } = useWindowDimensions();
  const fieldsLoadedDependency = useMemo(
    () => `${listOrigin}-${JSON.stringify(Object.keys(dataSet?.[0] || {}))}`,
    [`${listOrigin}-${JSON.stringify(Object.keys(dataSet?.[0] || {}))}`]
  );

  const dataCrc = useMemo(() => sum(dataSet), [dataSet]);
  // issue: #51
  // for this kind of filtering and render, the DataSource
  // is ok to live inside the component.'
  // not sure if it's optimal, but it should generate render only when needed
  //
  // also is interesting to investigate what is the optimal position
  // of this piece of code (DataSource), inside the component or outside,
  // at the beginning or at the end, etc.
  const anyPlainDataSource = useMemo(
    () =>
      new DataSource({
        store: new CustomStore({
          loadMode: "raw",
          key: fieldDefs[`_KEYOF_TABLE_${listOrigin}`] as string[],
          load: function () {
            if (!size(dataSet)) {
              return [];
            }
            return dataSet;
          },
          cacheRawData: true,
        }),
        paginate: false,
      }),
    [dataCrc, listOrigin]
  );

  const memoClassName = useMemo(() => {
    return `dx-swatch-jurnalKarma1 anyGrid ${
      !size(dataSet) && tsSrchBoxValue ? "spdanger" : ""
    }`;
  }, [size(dataSet), tsSrchBoxValue, xrender]);

  function* eInitializeColumns(): Operation<void> {
    yield* run(eLoadColumns);
    // we need to refresh the fieldDefinitions on load by setting them into the state
    // tryed to use the loadedColumnsRef.current but it was not working as expected
    const thisFieldDefs = pick(loadedColumnsRef.current, fieldDefs);
    setFieldDefinitions(thisFieldDefs);
    return;
  }
  //eslint-disable-next-line require-yield
  function* eSetLayout(specificLayoutID?: number): Operation<void> {
    let sid = 2;
    // on initial load we need to set the layoutID
    if (specificLayoutID === undefined) {
      const o = Object.assign({}, JSON.parse(JSON.stringify(theState)));
      specificLayoutID = o?.["layoutID"];
      if (specificLayoutID) {
        sid = specificLayoutID;
      }
    }
    // we load the layoutIDz
    if ([0, 1, 2].includes(sid)) {
      if (layoutID !== sid) {
        setLayoutID(sid);
      }
    } else {
      //fallback to fullscreen
      setLayoutID(2);
    }
    return;
  }
  //eslint-disable-next-line require-yield
  function* eLoadColumns(): Operation<void> {
    // it doesn't matter the state instance.
    let fieldList = Object.keys(dataSet?.[0] || {});
    if (!fieldList.length) {
      fieldList = Object.keys(theData?.[0] || {});
      if (!fieldList.length) {
        return;
      }
    }
    loadedColumnsRef.current = fieldList;
    return;
  }
  //eslint-disable-next-line require-yield
  function* eSetGridStateRef(nextState: IListState): Operation<void> {
    if (!tsGridRef.current) {
      return;
    }
    if (nextState["current"] !== undefined) {
      // ERROR ON SERIALIZATION
      delete nextState["current"];
      console.log(
        "Found error on nextState. Nested refs. This should not happen!!!(#0411)"
      );
    }
    tsGridRef.current.instance().state(nextState);
    return;
  }
  //eslint-disable-next-line require-yield
  function* eStartUpdate(): Operation<void> {
    yield* sleep(0);
    if (!tsGridRef.current) {
      return;
    }
    tsGridRef.current.instance().beginUpdate();
    return;
  }
  function* eEndUpdate(): Operation<void> {
    yield* sleep(0);
    if (!tsGridRef.current) {
      return;
    }
    tsGridRef.current.instance().endUpdate();
    return;
  }
  //eslint-disable-next-line require-yield
  function* eSaveNextExpected(whichState: TWhichState): Operation<void> {
    let ssc;
    switch (whichState) {
      case "initial":
        ssc = structuredClone(theState as IListState);
        local.expectedGridState = Object.freeze(ssc);
        break;
      case "current":
        {
          if (!tsGridRef.current) {
            return;
          }
          const currentPage4Layout = (structuredClone(savedStateRef.current) ||
            structuredClone(theState)) as IListState;
          const currentPageLayoutID = currentPage4Layout?.["layoutID"];
          const currentPageLayoutIDFromState = layoutID;
          const ssc_ = Object.assign({}, tsGridRef.current.instance().state(), {
            layoutID: currentPageLayoutIDFromState ?? currentPageLayoutID,
          });
          ssc = structuredClone(ssc_);
        }
        break;
      default:
        {
          if (typeof whichState === "object" && whichState !== null) {
            local.expectedGridState = Object.freeze(whichState) as IListState;
            ssc = structuredClone(whichState);
          }
        }
        break;
    }
    savedStateRef.current = Object.freeze(ssc) as IListState;
    return ssc;
  }
  //eslint-disable-next-line require-yield
  function* eApplyExpectedState(): Operation<void> {
    if (!tsGridRef.current) {
      return;
    }
    tsGridRef.current.instance().state(local.expectedGridState);
    return;
  }

  const filterAllWork = singleton(function* (
    array: any[] = [],
    fCriteria: { searchString: string | null } = {
      searchString: null,
    }
  ) {
    const { searchString } = fCriteria;
    const ou3 = yield* jscFilter(
      array,
      yielding((item: any) => {
        if (size(searchString) >= SEARCH_MIN_CHARS_NUMBER) {
          const weSearch = regexParts(parts(searchString || ""));
          return weSearch.some((rp) => {
            const fields = Object.values(item).filter(
              (x) => typeof x === "string"
            ) as string[];
            return fields.some((f: string) => rp.test(f));
          });
        } else {
          return true;
        }
      })
    );
    return ou3;
  }, theData);

  function* eProcessWorkData(
    array: typeof theData | null,
    { searchString }: { searchString: string }
  ) {
    if (!array) {
      array = theData;
    }
    if (size(searchString) < SEARCH_MIN_CHARS_NUMBER) {
      return array;
    } else {
      yield* sleep(0);
      const ds = yield* eCall(() => filterAllWork(array, { searchString }));
      console.log("ds", ds);
      return ds;
    }
  }
  //eslint-disable-next-line require-yield
  function* eSetProcessedData(
    ds: typeof theData | null,
    { searchString }: { searchString: string }
  ) {
    if (effRef5.current) {
      setTsSearchValue(searchString);
      setDataset(ds);
      searchStringRef.current = searchString;
      return true;
    }
    return false;
  }

  const eTApplyGridState = function* (
    whichState: TWhichState
  ): Operation<void> {
    yield* eStartUpdate();
    let nextState = whichState
      ? yield* eSaveNextExpected(whichState)
      : local.expectedGridState;
    nextState = (nextState || {}) as IListState;
    let li = 2;
    if (nextState && "layoutID" in nextState) {
      li = nextState["layoutID"] as number;
    }
    yield* eSetLayout(li);
    yield* sleep(10);
    yield* eLoadColumns();
    yield* eSetGridStateRef(nextState);
    yield* ensure(function* () {
      yield* sleep(10);
      yield* eEndUpdate();
    });
  };

  const eSearchTask = (text: string) =>
    function* (): Operation<void> {
      yield* eStartUpdate();
      yield* sleep(10);
      yield* eSaveNextExpected("current");
      yield* sleep(10);
      const dataset = yield* eProcessWorkData(null, { searchString: text });
      yield* eSetProcessedData(dataset, { searchString: text });
      yield* sleep(10);
      yield* eApplyExpectedState();
      yield* sleep(500); // imp //
      yield* ensure(function* () {
        yield* eEndUpdate();
      });
      return;
    };

  const eLoadDatasetDbg = function* (): Operation<void> {
    console.log("entering eLoadDataset");
    const labels = { fn: "eLoadDataset" };
    const searchString = tsSrchBoxValue;
    const q1 = yield* eStartUpdate();
    console.log("q1", q1);
    const q2 = yield* eLoadColumns();
    console.log("q2", q2);
    const q3 = yield* sleep(10);
    console.log("q3", q3);
    const q4 = yield* eSetLayout();
    console.log("q4", q4);
    // essential >
    const dataset = yield* eProcessWorkData(null, { searchString });
    console.log("dataset", dataset);
    const q5 = yield* eSetProcessedData(dataset, { searchString });
    console.log("q5", q5);
    // essential <
    yield* sleep(700);
    yield* eTApplyGridState("initial");
    yield* sleep(10);
    yield* ensure(function* () {
      const c = yield* eEndUpdate();
      console.log("c>>", c);
    });
    return;
  };
  const eLoadDataset = function* (): Operation<void> {
    const labels = { fn: "eLoadDataset" };
    const searchString = tsSrchBoxValue;
    yield* eStartUpdate();
    yield* eLoadColumns();
    yield* sleep(10);
    yield* eSetLayout();
    const dataset = yield* eProcessWorkData(null, { searchString });
    yield* eSetProcessedData(dataset, { searchString });
    yield* sleep(700);
    yield* eTApplyGridState("initial");
    yield* sleep(10);
    yield* ensure(function* () {
      yield* eEndUpdate();
    });
    return;
  };
  useEffect(() => {
    //0.
    effRef5.current = true;
    return () => {
      //max.important
      savedStateRef.current = {};
      effRef5.current = false;
    };
  });

  useEffect(() => {
    //1.
    // important to be before the next useEffect!
    // it renders and will reset the columns state
    run(eSetLayout);
  }, [theLoadedListID]);

  useEffect(() => {
    //2.
    run(eInitializeColumns);
  }, [fieldsLoadedDependency]);

  useEffect(() => {
    // 3.
    run(eLoadDataset);
  }, [theData]);

  useEffect(() => {
    //4.
    if (
      size(dataSet?.[0]) &&
      tsGridRef.current &&
      !savedStateRef.current &&
      !expectedGridState
    ) {
      run(() => eTApplyGridState("initial"));
    }
  }, [
    JSON.stringify(dataSet?.[0]),
    JSON.stringify(theState),
    savedStateRef.current,
  ]);

  // --- end of useEffects

  const debouncedSetSearchText = useDebouncedCallback((text: string) => {
    run(eSearchTask(text));
  }, 700);

  //  mark field by field
  function job_mark_field(mdText: any) {
    if (size(searchStringRef.current) < SEARCH_MIN_CHARS_NUMBER) {
      return md.render(mdText);
    }
    const regexParts = markRegexParts(searchStringRef.current);
    return mark(md.render(mdText))(regexParts);
  }
  //
  const tsOnCellPrepared = useCallback((e: any) => {
    if (e.rowType === "data") {
      e.cellElement.classList.add("tscell-narrative-1");
      e.cellElement.innerHTML = DOMPurify.sanitize(job_mark_field(e.text));
    }
  }, []);

  //to excel
  const tsOnExporting = (e: any) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      autoFilterEnabled: true,
    }).then(function () {
      // https://github.com/exceljs/exceljs#writing-xlsx
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          `FacturiEmise.xlsx`
        );
      });
    });
    e.cancel = true;
  };

  //export to pdf
  const exportGridToPdf = useCallback(() => {
    // openPdf({
    //   filename: `${FacturiEmise}.pdf`,
    //   showReportHeader: true,
    //   layoutParam: layoutID === 0 ? 'p' : 'l',
    // });
    void 0;
  }, [layoutID]);

  const loadState = () => {
    void 0; //* do not delete.
  };
  const saveState = () => {
    void 0; //* do not delete.
  };

  const tsOnContentReady = useCallback(() => {
    setTimeout(() => {
      document.getElementById("searchbox-random-id-971")?.focus();
    }, 1);
  }, []);

  const customShowColumnChooser = useCallback((e: any) => {
    if (!tsGridRef.current) return;
    tsGridRef.current.instance().showColumnChooser();
    const columnChooserView = (
      tsGridRef.current.instance as unknown as typeof e.component
    ).getView("columnChooserView");
    if (!columnChooserView._popupContainer) {
      columnChooserView._initializePopupContainer();
      columnChooserView.render();
    }
    columnChooserView._popupContainer.option("position", {
      of: tsGridRef.current.instance().element(),
      my: "right top",
      at: "right top",
      offset: "-20 85",
    });
  }, []);

  const initialize = useCallback((e: DataGridRef) => {
    tsGridRef.current = e;
  }, []);

  /* -------------------------------------------------------------------------- */

  // on page orientation change
  const changeGridLayout = () => {
    if (!tsGridRef.current) {
      return;
    }
    // the next layout:
    const i = (layoutID + 1) % 3;
    const currentState = structuredClone(tsGridRef.current.instance().state());
    // current state with the next layoutID:
    const newState = Object.freeze(
      Object.assign({}, currentState, {
        layoutID: i,
      })
    );
    run(() => eTApplyGridState(newState as IListState));
  };

  //reset
  const resetToInitialState = () => {
    run(() => eTApplyGridState("initial"));
  };
  //search
  const tsSrchBoxValueChanged = (e: any) => {
    debouncedSetSearchText(e.value);
  };

  return (
    <DataGrid
      // elementAttr={{
      //   'data-layoutID': layoutID,
      // }}
      id={`anyGrid-${listOrigin}`}
      ref={initialize}
      className={memoClassName}
      style={{ maxWidth: GRID_LAYOUTS?.[layoutID] }}
      height={drawerHeight || 0 - headersFooters}
      dataSource={anyPlainDataSource}
      showBorders={true}
      showRowLines={true}
      showColumnLines={true}
      columnAutoWidth={false}
      allowColumnReordering={true}
      allowColumnResizing={true}
      autoNavigateToFocusedRow={true}
      focusedRowEnabled={false}
      filterSyncEnabled={true}
      repaintChangesOnly={true}
      renderAsync={true}
      wordWrapEnabled={true}
      noDataText={""}
      // rowAlternationEnabled={true}
      onExporting={tsOnExporting}
      onContentReady={tsOnContentReady}
      onCellPrepared={tsOnCellPrepared}
    >
      <ColumnChooser
        enabled={true}
        title={t("tsgroupgrid_columnchooser")}
        width={280}
      />
      <ColumnFixing enabled={false} />
      <Editing
        mode={"form"}
        allowUpdating={false}
        allowDeleting={false}
        allowAdding={false}
      />
      <Export enabled={true} texts={{ exportAll: t("export_to_excel") }} />
      <FilterPanel visible={true} />
      <FilterRow visible={false} />
      <Grouping autoExpandAll={true} allowCollapsing={false} />
      <GroupPanel visible={true} emptyPanelText={t("tsgroupgrid_gropupanel")} />
      <HeaderFilter visible={true} searchMode={"contains"} allowSearch={true} />
      <LoadPanel enabled={false} shading={false} shadingColor="#fffefe" />
      <Paging enabled={true} pageSize={100} />
      <Scrolling mode={"infinite"} preloadEnabled={true} renderAsync={false} />
      <Selection mode="single" />
      <Sorting mode={"multiple"} />
      <StateStoring
        enabled={false}
        type="custom"
        customLoad={loadState}
        customSave={saveState}
      />
      <Toolbar>
        <Item
          location="before"
          widget="dxButton"
          options={{
            elementAttr: { id: "gridCloseButton" },
            id: "gridCloseButton",
            icon: "back",
            hint: "Inchide tabelul",
            onClick: (e: any) => {
              dispatch(
                uiListTempUiSetMenuOpened_afterSync({ MenuOpened: "TOGGLE" })
              );
            },
          }}
        />
        <Item location="before" name="groupPanel" />
        {/* <Item {...layoutButtonOpts({ layoutID, changeGridLayout })} /> */}
        <Item
          location="center"
          widget="dxButton"
          options={{
            elementAttr: { id: "grdLayoutButton" },
            id: "grdLayoutButton",
            icon:
              layoutID === 1 ? "doc" : layoutID === 2 ? "fullscreen" : "file",
            hint: "Formatul vizualizarii: Portret | Vedere | Ecran intreg",
            onClick: changeGridLayout,
          }}
        />
        {/* <Item {...initialSateButtonOpts({ resetToInitialState })} /> */}
        <Item
          location="center"
          widget="dxButton"
          options={{
            elementAttr: { id: "grdInitialStateButton" },
            id: "grdInitialStateButton",
            icon: "home",
            hint: "Revenire la starea initiala",
            onClick: resetToInitialState,
          }}
        />
        {/* <Item {...exportToPdfOpts({ exportGridToPdf })} /> */}
        <Item location="center" name="exportButton" />
        {/* <Item {...columnChooserButtonOpts({ customShowColumnChooser })} /> */}
        <Item
          location="center"
          widget="dxButton"
          options={{
            elementAttr: { id: "grdColumnChooserButton" },
            id: "grdColumnChooserButton",
            icon: "columnchooser",
            hint: "Selectarea coloanelor",
            onClick: customShowColumnChooser,
          }}
        />

        <Item
          location="after"
          widget="dxTextBox"
          options={{
            inputAttr: {
              id: "searchbox-random-id-971",
              ref: tsSearchBoxRef,
            },
            stylingMode: "underlined",
            width: 250,
            placeholder: "Cautare in tabel",
            value: tsSrchBoxValue,
            onValueChanged: tsSrchBoxValueChanged,
            valueChangeEvent: "keyup",
            showClearButton: true,
          }}
        />
      </Toolbar>
      {loadedColumnsRef.current.map((x: string, i) => {
        if (fieldDefinitions && fieldDefinitions[x]) {
          return (
            <Column
              key={`${listOrigin}-${x}-${i}`}
              dataField={x}
              {...(fieldDefinitions[x] as any)}
            />
          );
        } else {
          return null;
        }
      })}
      {listOrigin === CONST_LISTORIGIN_PROFORMA && (
        <Summary calculateCustomSummary={calculateTotalValues}>
          <GroupItem
            {...groupCustomItem({ name: "VLAFCT_FTVA_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VALFCT_TVA_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VALFCT_CUTVA_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VALINC_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "RESTINC_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VALFARATVACON_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VALTVACON_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VALFCON_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VALICON_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "RESTICON_TXT" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "CLIENT", label: "Clienti" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "CTRDEN", label: "Contracte" })}
            summaryType="sum"
          />
          <TotalItem
            {...totalsCount({ name: "NR_FCT", label: "Facturi" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VLAFCT_FTVA_TXT", label: "" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VALFCT_TVA_TXT", label: "" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VALFCT_CUTVA_TXT", label: "" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VALINC_TXT", label: "" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "RESTINC_TXT", label: "" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VALFARATVACON_TXT", label: "" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VALTVACON_TXT", label: "" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VALFCON_TXT", label: "" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VALICON_TXT", label: "" })}
            summaryType="sum"
          />
          <TotalItem
            {...groupCustomTotals({ name: "RESTICON_TXT", label: "" })}
            summaryType="sum"
          />
        </Summary>
      )}
      {listOrigin === CONST_LISTORIGIN_INVOICE && (
        <Summary calculateCustomSummary={calculateTotalValues}>
          <GroupItem
            {...groupCustomItem({ name: "VAL_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VAL_TVA_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "TOTAL_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "INCASAT_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "REST_DE_INCASAT_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VAL_EUR_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "VAL_TVA_EUR_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "TOTAL_EUR_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "INCASAT_EUR_TXT" })}
            summaryType="custom"
          />
          <GroupItem
            {...groupCustomItem({ name: "REST_DE_INCASAT_EUR_TXT" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "CLIENT", label: "Clienti" })}
            summaryType="custom"
          />
          <TotalItem
            {...totalsCount({ name: "NR_FF", label: "Facturi" })}
            summaryType="count"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VAL_TXT", label: "" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VAL_TVA_TXT", label: "" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "TOTAL_TXT", label: "" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "INCASAT_TXT", label: "" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "REST_DE_INCASAT_TXT", label: "" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VAL_EUR_TXT", label: "" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "VAL_TVA_EUR_TXT", label: "" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "TOTAL_EUR_TXT", label: "" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({ name: "INCASAT_EUR_TXT", label: "" })}
            summaryType="custom"
          />
          <TotalItem
            {...groupCustomTotals({
              name: "REST_DE_INCASAT_EUR_TXT",
              label: "",
            })}
            summaryType="custom"
          />
        </Summary>
      )}
    </DataGrid>
  );
};

export default memo(InvoiceGrid);
